import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import NotFoundError from './notfound-error';
import DuplicationError from './duplication-error';

const ErrorTitle = styled.p`
  font-weight: bold;
  font-size: 15px;
  margin-bottom: 15px;
`;

/**
 * This is the eror component that is rendered when there are problems
 * with the multiline XML file.
 *
 * This is rendered when there are multiple duplication errors
 * or a notfound error combined with duplication error(s)
 *
 * It also handles a single not found or duplication error
 */
const MultiLineError = ({
  numberOfDuplicates,
  numberNotFound,
  totalNumber,
  reference,
  intl,
  isCreating,
  createXMLRequest,
  customerCenter,
  multiLineRetriggerInfo,
  notFoundSerialNumbers,
  notFoundFunctionalLocationNumbers,
  numberFound,
}) => {
  const [selectedEquipments, setSelectedEquipments] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // Transform selectedEquipments to valid request with the correct ids
  const createDuplicateXML = () => {
    createXMLRequest({
      ...multiLineRetriggerInfo,
      duplicates: Object.keys(selectedEquipments).reduce((acc, key) => {
        acc[key] = multiLineRetriggerInfo?.duplicates[key][selectedEquipments[key]]?.id;
        return acc;
      }, {}),
    });
  };
  // If there are no duplicate, render one duplication error
  if (numberOfDuplicates === 0 && numberNotFound > 0) {
    return <NotFoundError
      title={intl.formatMessage({ id: 'fileUpload.NOT_FOUND_ITEMS_TITLE' })}
      numberNotFound={numberNotFound}
      totalNumber={totalNumber}
      reference={reference}
      intl={intl}
      isCreating={isCreating}
      createXMLRequest={createXMLRequest}
      action={multiLineRetriggerInfo}
      customerCenter={customerCenter}
      notFoundSerialNumbers={notFoundSerialNumbers}
      notFoundFunctionalLocationNumbers={notFoundFunctionalLocationNumbers}
      numberFound={numberFound}
    />;
    //  If there is a not found error without duplication errors, render only the notfound error
  } else if (numberOfDuplicates === 1 && numberNotFound === 0) {
    const [key] = Object.keys(multiLineRetriggerInfo?.duplicates);
    const [values] = Object.values(multiLineRetriggerInfo?.duplicates);
    return <DuplicationError
      title={intl.formatMessage({ id: 'fileUpload.DUPLICATE_TITLE' })}
      errors={[
        intl.formatMessage({ id: 'fileUpload.DUPLICATION_ERROR' }, { key, customerCenter }),
      ]}
      reference={reference}
      isCreating={isCreating}
      intl={intl}
      createXMLRequest={createDuplicateXML}
      selectedEquipment={
        !selectedEquipments[key] && selectedEquipments[key] !== 0 ? -1 : selectedEquipments[key]
      }
      actions={values || []}
      setSelectedEquipment={e => setSelectedEquipments(eq => ({
        ...eq,
        [key]: e.target.value,
      }))}
      numberFound={numberFound}
      isMulti={true}
    />;
  }

  // In all other cases render the paged error message.
  // Get the keys and values for the duplication errors,
  // if there is a notfound error the index of the data is equal to
  // the currentpage minus 2 else currentpage minus 1 because a
  // notfound error is always on the first page.
  const hasNotFoundEquipments = numberNotFound > 0;
  const index = hasNotFoundEquipments ? currentPage - 2 : currentPage - 1;
  const key = Object.keys(multiLineRetriggerInfo?.duplicates)[index];
  const values = Object.values(multiLineRetriggerInfo?.duplicates)[index];
  return <>
    <ErrorTitle>
      {intl.formatMessage(
        { id: 'fileUpload.MULTI_TITLE' },
        { totalProblems: numberOfDuplicates + numberNotFound },
      )}
    </ErrorTitle>
    {hasNotFoundEquipments && currentPage === 1 ?
      <NotFoundError
        title={`${intl.formatMessage(
          { id: 'fileUpload.PROBLEM_COUNT' },
          {
            totalProblems: numberOfDuplicates + numberNotFound,
            currentPage: 1,
          },
        )}: ${intl.formatMessage({ id: 'fileUpload.NOT_FOUND_ITEMS_TITLE' })}`}
        numberNotFound={numberNotFound}
        totalNumber={totalNumber}
        reference={reference}
        intl={intl}
        isCreating={isCreating}
        createXMLRequest={createXMLRequest}
        action={multiLineRetriggerInfo}
        customerCenter={customerCenter}
        notFoundSerialNumbers={notFoundSerialNumbers}
        notFoundFunctionalLocationNumbers={notFoundFunctionalLocationNumbers}
        numberFound={numberFound}
        hasNext={true}
        goToNextProblem={() => setCurrentPage(cp => cp + 1)}
      /> :
      <DuplicationError
        title={`${intl.formatMessage(
          { id: 'fileUpload.PROBLEM_COUNT' },
          {
            totalProblems: numberOfDuplicates + numberNotFound,
            currentPage,
          },
        )}: ${intl.formatMessage({ id: 'fileUpload.DUPLICATE_TITLE' })}`}
        errors={[
          intl.formatMessage({ id: 'fileUpload.DUPLICATION_ERROR' }, { key, customerCenter }),
        ]}
        reference={reference}
        isCreating={isCreating}
        intl={intl}
        createXMLRequest={createDuplicateXML}
        selectedEquipment={
          !selectedEquipments[key] && selectedEquipments[key] !== 0 ? -1 : selectedEquipments[key]
        }
        actions={values || []}
        setSelectedEquipment={e => setSelectedEquipments(eq => ({
          ...eq,
          [key]: e.target.value,
        }))}
        numberFound={numberFound}
        isMulti={true}
        hasNext={currentPage < numberOfDuplicates + numberNotFound}
        hasPrevious={currentPage > 1}
        goToNextProblem={() => setCurrentPage(cp => cp + 1)}
        goToPrevProblem={() => setCurrentPage(cp => cp - 1)}
      />
    }
  </>;
};

MultiLineError.propTypes = {
  numberOfDuplicates: PropTypes.number,
  notFoundSerialNumbers: PropTypes.array,
  notFoundFunctionalLocationNumbers: PropTypes.array,
  numberNotFound: PropTypes.number,
  totalNumber: PropTypes.number,
  reference: PropTypes.string,
  intl: PropTypes.object,
  isCreating: PropTypes.bool,
  createXMLRequest: PropTypes.func,
  multiLineRetriggerInfo: PropTypes.object,
  customerCenter: PropTypes.string,
  numberFound: PropTypes.number,
};

export default MultiLineError;
